import { MDText } from 'i18n-react';
import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import {
  ACCESS_LEVELS,
  SERVICE_ACCESSES,
  SERVICE_STATUSES,
  SERVICES_PRODUCTS_MAP,
  CURRENCIES,
  TIME_ZONES,
  TRADES,
} from './constants';

/**
 * Member product access level.
 * @typedef {'administrator' | 'member' | 'none'} MemberProductAccess
 */

/**
 * Access Level of a service.
 * @typedef {'projectMember' | 'projectAdmin' | 'accountAdmin' | 'acsProjectAdmin' | 'executive' | 'noAccess'} ProductAccessLevel
 */

/**
 * Status of a service.
 * @typedef {'active' | 'activating' | 'available' | 'deactivating' | 'activationFailed' | 'deactivationFailed' | 'inactive'} ServiceStatus
 */

/**
 * Status
 * @typedef {'active' | 'inactive'} Status
 */

/**
 *
 * @typedef {object} ServiceAccesses
 * @property {string} serviceName - The name of the service.
 * @property {boolean} access - The access level of the service.
 */

/**
 * Represents a service object.
 * @typedef {object} Service
 * @property {'assets' | 'costManagement'| 'designCollaboration'| 'documentManagement'| 'fieldManagement'| 'insight'| 'modelCoordination' | 'projectAdministration' | 'projectManagement' | 'quantification'} serviceName - The key of the product.
 * @property {ServiceStatus} status - The status of the service.
 */

/**
 * Product object.
 * @typedef {object} Product
 * @property {string} key - The key of the product.
 * @property {ProductAccessLevel} accessLevel - The status of the product.
 */

/**
 * Roles
 * @typedef {object} Roles
 * @property {string} id - The id of the role.
 * @property {string} name - The name of the role.
 * @property {string | null} key - The key of the role.
 * @property {Status} status - The status of the role.
 * @property {string} accountId - The account id of the role.
 * @property {string | null} templateId - The template id of the role.
 * @property {string} createdAt - The creation date of the role.
 * @property {string} updatedAt - The update date of the role.
 * @property {string} projectId - The project id of the role.
 * @property {string} memberGroupId - The member group id of the role.
 * @property {ServiceAccesses[]} serviceAccesses - The service accesses of the role.
 * @property {Product[]} products - The products of the role.
 */

/**
 * Access level of a service.
 * @typedef {object} AccessLevel
 * @property {string} key - The key of the access level.
 * @property {string} accessLevel - The access level.
 */

export const filterByActiveStatus = (collection) =>
  collection
    ? collection.filter(
        (element) =>
          element.status === SERVICE_STATUSES.ACTIVE ||
          element.status === SERVICE_STATUSES.ACTIVATING
      )
    : [];

export const mapToField = (collection, field) =>
  collection ? collection.map((element) => element[field]) : [];

export const getTimeZones = (locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));
  const map = TIME_ZONES.map((val) => ({
    value: val,
    label: T.translate(`TIME_ZONES.${val}`),
  }));

  return map;
};

export const getTrades = (locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));
  const map = TRADES.map((val) => ({
    value: val,
    label: T.translate(`TRADES.${val}`),
  }));

  return map;
};

export const getCurrencies = () =>
  CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }));

export const mapProjectTypesForSelectBox = (projectTypes, locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));

  const groupBy = (array, key) =>
    array.reduce((result, currentValue) => {
      const category =
        currentValue[key] !== null
          ? currentValue[key]
          : T.translate('PROJECT_TYPE_CATEGORY_OTHER');
      // eslint-disable-next-line no-param-reassign
      (result[category] = result[category] || []).push(currentValue);

      return result;
    }, {});

  if (projectTypes.length === 0) return [];

  const categories = groupBy(projectTypes, 'category');

  return Object.keys(categories).map((key) => ({
    label: key,
    options: categories[key]
      .map((projectType) => ({
        label: projectType.displayName,
        value: projectType.value,
      }))
      .filter(({ value }) => value !== 'Template Project'),
  }));
};

const sortTemplatesByName = (templates) => {
  const clean = (name) => name.trim().toLowerCase();
  return templates.sort(({ name: name1 }, { name: name2 }) =>
    clean(name1) > clean(name2) ? 1 : -1
  );
};

export const mapTemplatesToSelectGroupOptions = (templates, locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));

  const publishedTemplates = templates.filter(({ isPublished }) => isPublished);
  const unpublishedTemplates = templates.filter(
    ({ isPublished }) => !isPublished
  );

  if (publishedTemplates.length) {
    sortTemplatesByName(publishedTemplates);
    return [
      {
        label: T.translate('PUBLISHED_TEMPLATES_TITLE'),
        options: publishedTemplates,
        subtext: T.translate('PUBLISHED_TEMPLATES_DESCRIPTION'),
      },
      {
        label: T.translate('UNPUBLISHED_TEMPLATES_TITLE'),
        options: unpublishedTemplates,
        subtext: T.translate('UNPUBLISHED_TEMPLATES_DESCRIPTION'),
      },
    ];
  }

  // We only want to group the options if there are published templates to show.
  return unpublishedTemplates;
};

// TODO: This is a workaround to get the new construction API url
// since web platform does not have a method for this yet.
// Once the method is created by web platform we should use that one and remove this.
export const getConstructionApiUrl = () => {
  return getAdminApiUrl().replace(/bim360/i, 'construction');
};

/**
 * Product service object.
 * @typedef {object} ProductServices
 * @property {'cost' | 'insight'| 'docs'| 'takeoff'| 'designCollaboration'| 'projectAdministration'} key - The key of the product.
 * @property {ServiceStatus} status - The status of the product.
 */

/**
 * Gets project products from a list of project services.
 * @param {Service[]} projectServices - The array of project services.
 * @returns {ProductServices[]} - The array of products with their statuses.
 */
export const getProjectProductsFromServices = (projectServices) => {
  /**
   * An array of unique product keys extracted from a service-product mapping.
   * @type {string[]}
   */
  const products = SERVICES_PRODUCTS_MAP.reduce((result, map) => {
    if (!result.includes(map.product)) {
      result.push(map.product);
    }
    return result;
  }, []);
  return products.reduce((result, product) => {
    const servicesByProduct = SERVICES_PRODUCTS_MAP.filter(
      (map) => map.product === product
    ).map((e) => e.service);
    const isProductActive = servicesByProduct.some((service) =>
      projectServices.some(
        (projectService) =>
          service === projectService.serviceName &&
          (projectService.status === SERVICE_STATUSES.ACTIVE ||
            projectService.status === SERVICE_STATUSES.ACTIVATING)
      )
    );
    if (isProductActive) {
      result.push({ key: product, status: SERVICE_STATUSES.ACTIVE });
    }

    return result;
  }, []);
};

export const formatDate = (date) =>
  [
    date.getFullYear(),
    `0${date.getMonth() + 1}`.slice(-2),
    `0${date.getDate()}`.slice(-2),
  ].join('-');

export const buildCreateProjectCompanyRequest = (projectId, companyData) => [
  `${getAdminApiUrl()}/v1/projects/${projectId}/companies`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(companyData),
  },
];

export const buildAccountsRequest = () => {
  return `${getAdminApiUrl()}/v1/accounts?filter[accessLevels]=accountAdmin,projectAdmin&fields=name,accessLevels,hasAcsUnifiedProjects,isAccEligible,createCompanyAccessLevels&limit=200`;
};

export const buildProjectCompaniesRequest = (projectId, name) =>
  `${getAdminApiUrl()}/v1/projects/${projectId}/companies?limit=200&sort=name${
    name ? `&filter[name]=${name}` : ''
  }`;

export const isEmptyObject = (object) =>
  Object.values(object).every((x) => x === null || x === '');

export const removeExtraBlankSpaces = (str) => str.replace(/\s+/g, ' ').trim();

const hasAccess = (roles, product) => {
  return roles.some((role) => {
    return role.products
      ? role.products.some(
          (roleProduct) =>
            product === roleProduct.key &&
            (roleProduct.key !== PRODUCT_IDS.PROJECT_ADMINISTRATION ||
              (roleProduct.key === PRODUCT_IDS.PROJECT_ADMINISTRATION &&
                roleProduct.accessLevel === ACCESS_LEVELS.PROJECT_ADMIN))
        )
      : false;
  });
};

/**
 * Represents the access level of a member for multiple products.
 * @typedef {object} MemberProductsAccess
 * @property {string} key - The key of the product.
 * @property {MemberProductAccess} access - The access level of the product.
 */

/**
 * Omit none from selection
 * @typedef  {Exclude<MemberProductAccess, 'none'>} MemberProductAccessWithoutNone
 * @description Omitting none since it is not available in the UI.
 */

/**
 *
 * @typedef  {Pick<Roles, 'products'>} RolesWithProducts
 */

/**
 * Gets the member's products access based on roles and access level.
 * @param {object} options - The options object.
 * @param {MemberProductsAccess[]} options.currentMemberProductsAccess - The member's current products access.
 * @param {string[]} [options.defaultProducts=[]] - The default products.
 * @param {boolean} [options.isAccountAdmin] - The member's account admin access.
 * @param {Roles[]} [options.previousSelectedRoles=[]] - The previously selected roles.
 * @param {RolesWithProducts[]} options.selectedRoles - The selected roles.
 * @returns {MemberProductsAccess[]} - The member's products access based on roles and access level.
 */

export const getMemberProductsAccessByRoles = ({
  currentMemberProductsAccess,
  defaultProducts = [],
  isAccountAdmin,
  previousSelectedRoles = [],
  selectedRoles,
}) => {
  const rolesHaveAdminAccess = selectedRoles.some((selectedRole) => {
    return selectedRole.products?.length
      ? selectedRole.products.some((roleProduct) => {
          return roleProduct.accessLevel === ACCESS_LEVELS.PROJECT_ADMIN;
        })
      : false;
  });

  const rolesHaveDocsAccess = selectedRoles.some((selectedRole) => {
    return selectedRole.products?.length
      ? selectedRole.products.some((roleProduct) => {
          return (
            roleProduct.key === PRODUCT_IDS.DOCS &&
            roleProduct.accessLevel !== ACCESS_LEVELS.NO_ACCESS
          );
        })
      : false;
  });

  const rolesHadDocsAccess = previousSelectedRoles.some((selectedRole) => {
    return selectedRole.products?.length
      ? selectedRole.products.some((roleProduct) => {
          return (
            roleProduct.key === PRODUCT_IDS.DOCS &&
            roleProduct.accessLevel !== ACCESS_LEVELS.NO_ACCESS
          );
        })
      : false;
  });

  /**
   * Check if the member has admin access in any of the selected roles
   * or previously had admin access
   */
  const memberHasAdminAccess = rolesHaveAdminAccess || isAccountAdmin;

  const memberAccessLevel = memberHasAdminAccess
    ? SERVICE_ACCESSES.ADMIN
    : SERVICE_ACCESSES.MEMBER;

  const newMemberProductsAccess = currentMemberProductsAccess.map(
    (memberProductAccess) => {
      const productHasAccessByRoles = hasAccess(
        selectedRoles,
        memberProductAccess.key
      );
      const productHadAccessByRoles = hasAccess(
        previousSelectedRoles,
        memberProductAccess.key
      );

      const productManuallyActivated =
        memberProductAccess.key !== PRODUCT_IDS.PROJECT_ADMINISTRATION &&
        memberProductAccess.access !== SERVICE_ACCESSES.NONE &&
        !productHasAccessByRoles &&
        !productHadAccessByRoles;

      const defaultProductMatchMemberProductAccess = defaultProducts.some(
        (defaultProduct) => defaultProduct === memberProductAccess.key
      );

      const memberHasAccessToProduct =
        productHasAccessByRoles ||
        (productManuallyActivated &&
          !rolesHaveDocsAccess &&
          !rolesHadDocsAccess) ||
        (memberProductAccess.key === PRODUCT_IDS.PROJECT_ADMINISTRATION &&
          memberHasAdminAccess) ||
        defaultProductMatchMemberProductAccess;

      return {
        key: memberProductAccess.key,
        access: memberHasAccessToProduct
          ? memberAccessLevel
          : SERVICE_ACCESSES.NONE,
      };
    }
  );

  const insightAccess = newMemberProductsAccess.find(
    (productAccess) => productAccess.key === PRODUCT_IDS.INSIGHT
  )?.access;

  const needsInsightAccess = newMemberProductsAccess.some(
    (productAccess) =>
      productAccess.key !== PRODUCT_IDS.INSIGHT &&
      productAccess.access !== SERVICE_ACCESSES.NONE
  );
  if (insightAccess === SERVICE_ACCESSES.NONE && needsInsightAccess) {
    const insightIndex = newMemberProductsAccess.findIndex(
      (productAccess) => productAccess.key === PRODUCT_IDS.INSIGHT
    );
    newMemberProductsAccess[insightIndex].access = memberAccessLevel;
  }
  const hasInsightAccess = newMemberProductsAccess.some(
    (productAccess) =>
      productAccess.key === PRODUCT_IDS.INSIGHT &&
      productAccess.access !== SERVICE_ACCESSES.NONE
  );

  const hasDocsAccess = newMemberProductsAccess.some(
    (productAccess) =>
      productAccess.key === PRODUCT_IDS.DOCS &&
      productAccess.access !== SERVICE_ACCESSES.NONE
  );

  const shouldRemoveMember =
    !hasInsightAccess || (!hasDocsAccess && !memberHasAdminAccess);

  return shouldRemoveMember ? null : newMemberProductsAccess;
};
